import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  PlaylistAdd as SeedIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { EmailEvent, EmailEventTemplateUpdate, EmailEventsByModule, RecipientType } from '../../../types/emailEvent';
import { SmtpSetting } from '../../../types/smtpSetting';
import { emailEventsAPI, smtpSettingsAPI } from '../../../services/api';
import { getRoles, Role } from '../../../services/rbac';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`module-tabpanel-${index}`}
      aria-labelledby={`module-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const moduleLabels: Record<string, string> = {
  commercial: 'Commercial',
  hr: 'Ressources Humaines',
  stock: 'Stock',
  system: 'Système',
};

const moduleColors: Record<string, string> = {
  commercial: '#4CAF50',
  hr: '#9C27B0',
  stock: '#2196F3',
  system: '#607D8B',
};

const EmailEventsManagement: React.FC = () => {
  const [eventsByModule, setEventsByModule] = useState<EmailEventsByModule[]>([]);
  const [smtpSettings, setSmtpSettings] = useState<SmtpSetting[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EmailEvent | null>(null);
  const [templateData, setTemplateData] = useState<EmailEventTemplateUpdate>({
    template_subject: '',
    template_body: '',
  });
  const [selectedSmtpId, setSelectedSmtpId] = useState<number | null>(null);
  const [recipientType, setRecipientType] = useState<RecipientType>('dynamic');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientVariable, setRecipientVariable] = useState('');
  const [recipientRoleId, setRecipientRoleId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  useEffect(() => {
    fetchEvents();
    fetchSmtpSettings();
    fetchRoles();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await emailEventsAPI.getByModule();
      setEventsByModule(data);
    } catch (error) {
      showError('Erreur lors du chargement des événements email');
      console.error('Error fetching email events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const data = await smtpSettingsAPI.getAll();
      setSmtpSettings(data.filter(s => s.is_active));
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await getRoles();
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleToggle = async (event: EmailEvent) => {
    try {
      await emailEventsAPI.toggle(event.id, { is_enabled: !event.is_enabled });
      showSuccess(`Événement ${!event.is_enabled ? 'activé' : 'désactivé'} avec succès`);
      fetchEvents();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleOpenTemplateDialog = (event: EmailEvent) => {
    setSelectedEvent(event);
    setTemplateData({
      template_subject: event.template_subject || '',
      template_body: event.template_body || '',
    });
    setSelectedSmtpId(event.smtp_setting_id || null);
    setRecipientType(event.recipient_type || 'dynamic');
    setRecipientEmail(event.recipient_email || '');
    setRecipientVariable(event.recipient_variable || '');
    setRecipientRoleId(event.recipient_role_id || null);
    setTemplateDialogOpen(true);
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setSelectedEvent(null);
    setTemplateData({ template_subject: '', template_body: '' });
    setSelectedSmtpId(null);
    setRecipientType('dynamic');
    setRecipientEmail('');
    setRecipientVariable('');
    setRecipientRoleId(null);
  };

  const handleSaveTemplate = async () => {
    if (!selectedEvent) return;

    // Validation
    if (recipientType === 'fixed' && !recipientEmail.trim()) {
      showError('Veuillez saisir une adresse email pour les destinataires fixes');
      return;
    }
    if (recipientType === 'dynamic' && !recipientVariable) {
      showError('Veuillez sélectionner la variable contenant l\'email du destinataire');
      return;
    }
    if (recipientType === 'role' && !recipientRoleId) {
      showError('Veuillez sélectionner un rôle');
      return;
    }

    try {
      setLoading(true);
      // Update template
      await emailEventsAPI.updateTemplate(selectedEvent.id, templateData);

      // Update all other settings
      await emailEventsAPI.update(selectedEvent.id, {
        smtp_setting_id: selectedSmtpId || undefined,
        recipient_type: recipientType,
        recipient_email: recipientType === 'fixed' ? recipientEmail : undefined,
        recipient_variable: recipientType === 'dynamic' ? recipientVariable : undefined,
        recipient_role_id: recipientType === 'role' && recipientRoleId ? recipientRoleId : undefined,
      });

      showSuccess('Configuration mise à jour avec succès');
      handleCloseTemplateDialog();
      fetchEvents();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSeedEvents = async () => {
    showConfirm(
      'Cette action va créer les événements email prédéfinis. Les événements existants ne seront pas modifiés. Continuer ?',
      async () => {
        try {
          setSeeding(true);
          const result = await emailEventsAPI.seed();
          showSuccess(`${result.created} événement(s) créé(s), ${result.skipped} ignoré(s)`);
          fetchEvents();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setSeeding(false);
        }
      },
      {
        title: 'Initialiser les événements',
        confirmText: 'Initialiser',
        cancelText: 'Annuler',
      }
    );
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('template-body') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateData.template_body;
      const newText = text.substring(0, start) + `{{${variable}}}` + text.substring(end);
      setTemplateData({ ...templateData, template_body: newText });
    } else {
      setTemplateData({
        ...templateData,
        template_body: templateData.template_body + `{{${variable}}}`,
      });
    }
  };

  const getRecipientDisplay = (event: EmailEvent) => {
    switch (event.recipient_type) {
      case 'dynamic':
        return event.recipient_variable ? `Variable: {{${event.recipient_variable}}}` : 'Non configuré';
      case 'fixed':
        return event.recipient_email || 'Non configuré';
      case 'role':
        return event.recipient_role ? `Rôle: ${event.recipient_role.name}` : 'Non configuré';
      default:
        return 'Non configuré';
    }
  };

  const renderEventsTable = (events: EmailEvent[]) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Événement</TableCell>
            <TableCell>Destinataire</TableCell>
            <TableCell>Serveur SMTP</TableCell>
            <TableCell align="center">Activé</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id}>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {event.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {event.code}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {getRecipientDisplay(event)}
                </Typography>
              </TableCell>
              <TableCell>
                {event.smtp_setting ? (
                  <Chip
                    label={event.smtp_setting.name}
                    size="small"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Par défaut
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                <Switch
                  checked={event.is_enabled}
                  onChange={() => handleToggle(event)}
                  color="primary"
                />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Configurer">
                  <IconButton
                    onClick={() => handleOpenTemplateDialog(event)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {events.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                Aucun événement dans ce module
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Événements Email</Typography>
        <Button
          variant="outlined"
          startIcon={<SeedIcon />}
          onClick={handleSeedEvents}
          disabled={seeding}
        >
          {seeding ? 'Initialisation...' : 'Initialiser les événements'}
        </Button>
      </Box>

      {eventsByModule.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Aucun événement email configuré. Cliquez sur "Initialiser les événements" pour créer les événements prédéfinis.
        </Alert>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              {eventsByModule.map((group, index) => (
                <Tab
                  key={group.module}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: moduleColors[group.module] || '#757575',
                        }}
                      />
                      {moduleLabels[group.module] || group.module}
                      <Chip
                        label={group.events.length}
                        size="small"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={getModuleTableTheme('configuration')}>
            {eventsByModule.map((group, index) => (
              <TabPanel key={group.module} value={tabValue} index={index}>
                {renderEventsTable(group.events)}
              </TabPanel>
            ))}
          </Box>
        </>
      )}

      {/* Template Editor Dialog */}
      <Dialog open={templateDialogOpen} onClose={handleCloseTemplateDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Configurer - {selectedEvent?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Recipient Configuration */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
                    Destinataire
                  </FormLabel>
                  <RadioGroup
                    value={recipientType}
                    onChange={(e) => setRecipientType(e.target.value as RecipientType)}
                  >
                    <FormControlLabel
                      value="dynamic"
                      control={<Radio />}
                      label="Dynamique (variable du contexte)"
                    />
                    <FormControlLabel
                      value="fixed"
                      control={<Radio />}
                      label="Adresse(s) fixe(s)"
                    />
                    <FormControlLabel
                      value="role"
                      control={<Radio />}
                      label="Tous les utilisateurs d'un rôle"
                    />
                  </RadioGroup>
                </FormControl>

                {recipientType === 'dynamic' && selectedEvent?.variables && (
                  <FormControl fullWidth variant="filled" sx={{ mt: 2 }}>
                    <InputLabel>Variable contenant l'email</InputLabel>
                    <Select
                      value={recipientVariable}
                      onChange={(e) => setRecipientVariable(e.target.value)}
                    >
                      {selectedEvent.variables
                        .filter(v => v.toLowerCase().includes('email'))
                        .map((variable) => (
                          <MenuItem key={variable} value={variable}>
                            {`{{${variable}}}`}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}

                {recipientType === 'fixed' && (
                  <TextField
                    variant="filled"
                    fullWidth
                    label="Adresse(s) email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="email@exemple.com, autre@exemple.com"
                    helperText="Séparez les adresses par des virgules pour plusieurs destinataires"
                    sx={{ mt: 2 }}
                  />
                )}

                {recipientType === 'role' && (
                  <FormControl fullWidth variant="filled" sx={{ mt: 2 }}>
                    <InputLabel>Rôle</InputLabel>
                    <Select
                      value={recipientRoleId || ''}
                      onChange={(e) => setRecipientRoleId(e.target.value as number || null)}
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Paper>
            </Grid>

            {/* SMTP Server */}
            <Grid item xs={12}>
              <FormControl fullWidth variant="filled">
                <InputLabel>Serveur SMTP</InputLabel>
                <Select
                  value={selectedSmtpId || ''}
                  onChange={(e) => setSelectedSmtpId(e.target.value as number || null)}
                >
                  <MenuItem value="">
                    <em>Par défaut (serveur principal)</em>
                  </MenuItem>
                  {smtpSettings.map((smtp) => (
                    <MenuItem key={smtp.id} value={smtp.id}>
                      {smtp.name} ({smtp.from_email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Template Subject */}
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                label="Sujet"
                value={templateData.template_subject}
                onChange={(e) => setTemplateData({ ...templateData, template_subject: e.target.value })}
                placeholder="Ex: Confirmation de votre commande {{reference}}"
              />
            </Grid>

            {/* Template Body */}
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                multiline
                rows={8}
                label="Corps du message"
                id="template-body"
                value={templateData.template_body}
                onChange={(e) => setTemplateData({ ...templateData, template_body: e.target.value })}
                placeholder="Bonjour {{client_name}},&#10;&#10;Votre commande {{reference}} a été confirmée."
              />
            </Grid>

            {/* Available Variables */}
            {selectedEvent?.variables && selectedEvent.variables.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InfoIcon fontSize="small" color="info" />
                  <Typography variant="body2" color="text.secondary">
                    Variables disponibles (cliquez pour insérer) :
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedEvent.variables.map((variable) => (
                    <Chip
                      key={variable}
                      label={`{{${variable}}}`}
                      size="small"
                      onClick={() => insertVariable(variable)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateDialog}>Annuler</Button>
          <Button onClick={handleSaveTemplate} variant="contained" disabled={loading}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default EmailEventsManagement;
